import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Progress } from "./ui/progress";
import { Badge } from "./ui/badge";
import { Phone, AlertTriangle, TrendingUp, Hash, ArrowRight, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TelecomUsageWidget() {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/platform/twilio-usage`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!cancelled) setUsage(d);
      })
      .catch(() => {
        if (!cancelled) setUsage(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <Card className="border-slate-200" data-testid="telecom-usage-widget">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-bold flex items-center gap-2">
            <Phone className="w-4 h-4 text-blue-600" />
            Telecom Usage
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-3">
            <div className="h-2 bg-slate-200 rounded" />
            <div className="h-8 bg-slate-100 rounded" />
            <div className="h-12 bg-slate-100 rounded" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!usage) return null;

  const minutesUsed = usage.allotment?.minutes_used ?? 0;
  const minutesIncluded = usage.allotment?.included_minutes ?? 0;
  const minutesRemaining = usage.allotment?.minutes_remaining ?? 0;
  const overageMinutes = usage.allotment?.overage_minutes ?? 0;
  const overageCost = usage.allotment?.overage_cost ?? 0;
  const overageRate = usage.allotment?.overage_per_minute ?? 0;
  const numbersCount = usage.phone_numbers?.count ?? 0;
  const numbersFee = usage.phone_numbers?.monthly_fee ?? 0;
  const numbersCost = usage.phone_numbers?.total ?? 0;
  const totalCost = usage.total_telecom_cost ?? 0;

  const pct = minutesIncluded > 0 ? Math.min(100, (minutesUsed / minutesIncluded) * 100) : 0;
  const isOver = minutesUsed > minutesIncluded;
  const isNear = pct >= 80 && !isOver;

  const barColor = isOver ? "bg-red-500" : isNear ? "bg-amber-500" : "bg-blue-500";

  return (
    <Card
      className={`relative overflow-hidden ${isOver ? "border-red-200" : "border-slate-200"}`}
      data-testid="telecom-usage-widget"
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <CardTitle className="text-base font-bold flex items-center gap-2">
              <Phone className="w-4 h-4 text-blue-600" />
              Telecom Usage
            </CardTitle>
            <CardDescription className="text-xs">
              {usage.period?.month} · {usage.plan?.name || "—"} plan
            </CardDescription>
          </div>
          <Link
            to="/calls"
            className="text-xs text-blue-600 hover:underline flex items-center gap-0.5"
          >
            View calls
            <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Minute allotment progress */}
        <div>
          <div className="flex items-center justify-between text-xs mb-1.5">
            <span className="text-slate-600 font-medium">Voice minutes</span>
            <span className={`font-semibold ${isOver ? "text-red-600" : isNear ? "text-amber-600" : "text-slate-700"}`}>
              {minutesUsed.toLocaleString()} / {minutesIncluded.toLocaleString()}
            </span>
          </div>
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <div
              className={`h-full ${barColor} transition-all`}
              style={{ width: `${pct}%` }}
              data-testid="usage-progress-bar"
            />
          </div>
          <div className="flex items-center justify-between text-xs mt-1.5 text-slate-500">
            <span>{usage.calls?.count ?? 0} calls this month</span>
            <span>
              {isOver
                ? `${overageMinutes.toLocaleString()} min over`
                : `${minutesRemaining.toLocaleString()} min remaining`}
            </span>
          </div>
        </div>

        {/* Alert banner */}
        {isOver && (
          <div className="flex items-start gap-2 p-2.5 rounded-md bg-red-50 border border-red-200" data-testid="usage-alert">
            <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-red-800">
              You&apos;re past your monthly allotment. Each additional minute costs ${overageRate.toFixed(2)} and will appear on your next invoice.
            </div>
          </div>
        )}
        {isNear && !isOver && (
          <div className="flex items-start gap-2 p-2.5 rounded-md bg-amber-50 border border-amber-200" data-testid="usage-alert">
            <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-amber-800">
              You&apos;ve used {Math.round(pct)}% of your monthly minutes.
            </div>
          </div>
        )}

        {/* Cost breakdown */}
        <div className="rounded-md border border-slate-200 divide-y divide-slate-100">
          <div className="flex items-center justify-between px-3 py-2 text-xs">
            <span className="text-slate-600 flex items-center gap-1.5">
              <TrendingUp className="w-3.5 h-3.5" />
              Overage ({overageMinutes.toLocaleString()} min × ${overageRate.toFixed(2)})
            </span>
            <span className="font-mono font-semibold text-slate-800">${overageCost.toFixed(2)}</span>
          </div>
          <div className="flex items-center justify-between px-3 py-2 text-xs">
            <span className="text-slate-600 flex items-center gap-1.5">
              <Hash className="w-3.5 h-3.5" />
              Phone numbers ({numbersCount} × ${numbersFee.toFixed(2)})
            </span>
            <span className="font-mono font-semibold text-slate-800">${numbersCost.toFixed(2)}</span>
          </div>
          <div className="flex items-center justify-between px-3 py-2 text-xs bg-slate-50">
            <span className="text-slate-700 font-semibold flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5 text-blue-600" />
              Next-invoice estimate
            </span>
            <span className="font-mono font-bold text-slate-900" data-testid="next-invoice-estimate">
              ${totalCost.toFixed(2)}
            </span>
          </div>
        </div>

        <p className="text-[11px] text-slate-400 leading-snug">
          Estimate excludes base subscription. Calculated from current month-to-date usage.{" "}
          <Link to="/help" className="text-blue-500 hover:underline">
            Learn more
          </Link>
        </p>
      </CardContent>
    </Card>
  );
}

export function TelecomUsageInline() {
  /* Compact inline variant — single line for dense dashboards. */
  const [usage, setUsage] = useState(null);
  useEffect(() => {
    fetch(`${API}/platform/twilio-usage`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then(setUsage)
      .catch(() => {});
  }, []);
  if (!usage) return null;
  const used = usage.allotment?.minutes_used ?? 0;
  const incl = usage.allotment?.included_minutes ?? 0;
  const cost = usage.total_telecom_cost ?? 0;
  return (
    <Badge variant="secondary" className="gap-1">
      <Phone className="w-3 h-3" />
      {used}/{incl} min · ${cost.toFixed(2)}
    </Badge>
  );
}
